import { createContext, useContext, useState, useCallback, useEffect } from "react";
import {
  dummyAgents,
  dummyConversations,
  dummyLeads,
  dummyKnowledgeBase,
} from "../data/dummyData";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [agents, setAgents] = useState(dummyAgents);
  const [conversations, setConversations] = useState(dummyConversations);
  const [leads, setLeads] = useState(dummyLeads);
  const [knowledgeBase, setKnowledgeBase] = useState(dummyKnowledgeBase);
  const [newAgentData, setNewAgentData] = useState({
    name: "",
    businessType: "",
    businessDescription: "",
    language: "English",
    behavior: "",
    llmModel: "gpt-4o-mini",
    features: {
      autoReply: true,
      leadCapture: true,
      appointmentBooking: false,
      gmailIntegration: false,
      humanHandoff: true,
    },
    whatsappNumber: "",
    respondTo: "all",
    whitelist: [],
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Auth functions
  const checkAuth = useCallback(async () => {
    try {
      setIsAuthLoading(true);
      const response = await fetch(`${API_URL}/auth/user`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        // Only set user if authenticated AND user data exists
        if (data.authenticated && data.user) {
          setUser(data.user);
          return true;
        } else {
          setUser(null);
          return false;
        }
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      return false;
    } finally {
      setIsAuthLoading(false);
    }
  }, [API_URL]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signOut = useCallback(async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
  }, [API_URL]);

  // Agent functions
  const createAgent = useCallback((agentData) => {
    const newAgent = {
      id: `agent-${Date.now()}`,
      ...agentData,
      status: "live",
      whatsappConnected: true,
      createdAt: new Date().toISOString(),
      stats: {
        totalConversations: 0,
        activeConversations: 0,
        leadsCollected: 0,
        messagesHandled: 0,
      },
    };
    setAgents((prev) => [...prev, newAgent]);
    setConversations((prev) => ({ ...prev, [newAgent.id]: [] }));
    setLeads((prev) => ({ ...prev, [newAgent.id]: [] }));
    setKnowledgeBase((prev) => ({ ...prev, [newAgent.id]: [] }));
    return newAgent;
  }, []);

  const updateAgent = useCallback((agentId, updates) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.id === agentId ? { ...agent, ...updates } : agent
      )
    );
  }, []);

  const toggleAgentStatus = useCallback((agentId) => {
    setAgents((prev) =>
      prev.map((agent) =>
        agent.id === agentId
          ? { ...agent, status: agent.status === "live" ? "paused" : "live" }
          : agent
      )
    );
  }, []);

  const getAgent = useCallback(
    (agentId) => {
      return agents.find((agent) => agent.id === agentId);
    },
    [agents]
  );

  // Conversation functions
  const getAgentConversations = useCallback(
    (agentId) => {
      return conversations[agentId] || [];
    },
    [conversations]
  );

  const getConversation = useCallback(
    (agentId, conversationId) => {
      const agentConvs = conversations[agentId] || [];
      return agentConvs.find((conv) => conv.id === conversationId);
    },
    [conversations]
  );

  const addMessage = useCallback((agentId, conversationId, message) => {
    setConversations((prev) => ({
      ...prev,
      [agentId]: prev[agentId].map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              messages: [...conv.messages, message],
              lastMessage: message.text,
              lastMessageTime: message.time,
            }
          : conv
      ),
    }));
  }, []);

  // Leads functions
  const getAgentLeads = useCallback(
    (agentId) => {
      return leads[agentId] || [];
    },
    [leads]
  );

  const addLead = useCallback((agentId, lead) => {
    const newLead = {
      id: `lead-${Date.now()}`,
      ...lead,
      capturedAt: new Date().toISOString(),
    };
    setLeads((prev) => ({
      ...prev,
      [agentId]: [...(prev[agentId] || []), newLead],
    }));
  }, []);

  // Knowledge Base functions
  const getAgentKnowledge = useCallback(
    (agentId) => {
      return knowledgeBase[agentId] || [];
    },
    [knowledgeBase]
  );

  const addKnowledgeFile = useCallback((agentId, file) => {
    const newFile = {
      id: `kb-${Date.now()}`,
      name: file.name,
      type: file.type || "application/octet-stream",
      size: file.size || 0,
      uploadedAt: new Date().toISOString(),
    };
    setKnowledgeBase((prev) => ({
      ...prev,
      [agentId]: [...(prev[agentId] || []), newFile],
    }));
  }, []);

  const removeKnowledgeFile = useCallback((agentId, fileId) => {
    setKnowledgeBase((prev) => ({
      ...prev,
      [agentId]: prev[agentId].filter((file) => file.id !== fileId),
    }));
  }, []);

  // New agent wizard functions
  const updateNewAgentData = useCallback((updates) => {
    setNewAgentData((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetNewAgentData = useCallback(() => {
    setNewAgentData({
      name: "",
      businessType: "",
      businessDescription: "",
      language: "English",
      behavior: "",
      llmModel: "gpt-4o-mini",
      features: {
        autoReply: true,
        leadCapture: true,
        appointmentBooking: false,
        gmailIntegration: false,
        humanHandoff: true,
      },
      whatsappNumber: "",
      respondTo: "all",
      whitelist: [],
    });
  }, []);

  const value = {
    user,
    isAuthLoading,
    checkAuth,
    signOut,
    agents,
    createAgent,
    updateAgent,
    toggleAgentStatus,
    getAgent,
    getAgentConversations,
    getConversation,
    addMessage,
    getAgentLeads,
    addLead,
    getAgentKnowledge,
    addKnowledgeFile,
    removeKnowledgeFile,
    newAgentData,
    updateNewAgentData,
    resetNewAgentData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
